import { Link, useParams } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SmbAttachmentStatus, type Transaction } from "@/apis/gql/types";
import { BankReauthAlert } from "@/components/shared/alerts/bank-reauth-alert";
import { TransactionsBadge } from "@/components/shared/app-navigation/sidebar/badges/company";
import AIProcessingCard from "@/components/shared/cards/ai-processing-card";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import UploadProgressBox from "@/features/upload-progress-box/UploadProgressBox";
import { cn } from "@/lib/utils";
import { useAddInvoiceToTransaction } from "@/queries/transactions/add-invoice";
import { useApproveSmbTransactionAttachments } from "@/queries/transactions/approve-smb-transaction-attachments";
import { useDeleteInvoiceFromTransaction } from "@/queries/transactions/delete-invoice";
import { useUpdateBankTransaction } from "@/queries/transactions/update-bank-transaction";
import { smbTransactionsTodosFilter } from "@/queries/transactions/utils/transactions-todos-filter";

import { TransactionsTodos } from "../../shared/transactions-todos";
import { useTransactionsTodosTable } from "../../shared/use-transactions-todos-table";

import { useArrayQueryState } from "@/hooks/useArrayQueryState";
import { InvoiceMatchingPanel } from "../invoice-matching-panel";
import { columns as todosColumns } from "../transaction-todos/transactions-todos-columns";
import { TableActionBar } from "./transactions-todos-action-bar";

export default function Transactions() {
  const { t } = useTranslation();
  const { company: companyId } = useParams({
    from: "/_authenticated/$company",
  });
  const [updateBankTransaction] = useUpdateBankTransaction();
  const [approveSmbTransactionAttachments] =
    useApproveSmbTransactionAttachments();
  const [addInvoiceToTransaction] = useAddInvoiceToTransaction();
  const [deleteInvoiceFromTransaction] = useDeleteInvoiceFromTransaction();

  const [selectedTransactionIds, setSelectedTransactionIds] =
    useArrayQueryState("selected-transaction-ids");

  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<
    Record<string, boolean>
  >({});

  const transactionTodos = useTransactionsTodosTable({
    storageKey: "smb-transaction-todos",
    defaultFilters: smbTransactionsTodosFilter,
    columns: todosColumns,
    rowSelection: selectedTransactionIds,
    onRowSelectionChange: setSelectedTransactionIds,
  });

  const transactionsForPanel = transactionTodos.table
    .getFilteredSelectedRowModel()
    .rows.map((row) => row.original) as Transaction[];

  return (
    <>
      <div className="h-full flex">
        <div className="flex flex-1">
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex flex-col px-4 gap-2 bg-background">
              <div className="flex gap-4 overflow-x-auto whitespace-nowrap">
                <BankReauthAlert className="pb-4 max-w-md h-full flex-shrink-0" />
                <AIProcessingCard screen="txn-todos" className="mb-4" />
              </div>
            </div>
            <div className="flex-1 flex">
              <div className="flex flex-col min-w-0 w-full relative">
                <div>
                  <NavigationMenu className="px-4 bg-background max-w-full justify-start border-b">
                    <NavigationMenuList className="space-x-0">
                      <NavigationMenuItem>
                        <Link
                          to="/$company/unternehmen/transaktionen/todos"
                          params={{ company: companyId }}
                        >
                          <NavigationMenuLink
                            active
                            className={cn(
                              "text-gray-500 flex items-center pb-3 px-6 gap-4 font-medium text-lg",
                              "inline-flex justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                              "border-b-[3px] text-primary border-primary cursor-default",
                            )}
                            onClick={(e) => e.preventDefault()}
                          >
                            {t("transactions.tabs.todos")}
                            <TransactionsBadge />
                          </NavigationMenuLink>
                        </Link>
                      </NavigationMenuItem>
                      <NavigationMenuItem>
                        <Link
                          to="/$company/unternehmen/transaktionen"
                          params={{ company: companyId }}
                        >
                          <NavigationMenuLink
                            className={cn(
                              "text-gray-500 flex items-center pb-3 px-6 gap-2 font-medium text-lg rounded-none",
                              "inline-flex justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                              "data-[active=true]:border-b-[3px] data-[active=true]:border-primary",
                            )}
                          >
                            {t("transactions.tabs.all_transactions")}
                          </NavigationMenuLink>
                        </Link>
                      </NavigationMenuItem>
                    </NavigationMenuList>
                  </NavigationMenu>
                </div>
                <div className="flex-1 flex flex-col">
                  <div className="flex-1 px-4 pb-4">
                    <TransactionsTodos {...transactionTodos}>
                      <TransactionsTodos.ActionBar>
                        <TableActionBar
                          onMarkAsLost={handleMarkAsLost}
                          onMarkAsReviewed={handleMarkAsReviewed}
                          onMatch={handleMatch}
                          selectedInvoices={selectedInvoiceIds}
                          table={transactionTodos.table}
                        />
                      </TransactionsTodos.ActionBar>
                    </TransactionsTodos>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <AnimatePresence mode="wait">
            {transactionsForPanel.length > 0 && (
              <motion.div
                key="invoice-matching-panel-wrapper"
                className="bg-background z-10 overflow-hidden"
                style={{
                  width: "50%",
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.3,
                  ease: [0.4, 0, 0.2, 1],
                }}
              >
                <InvoiceMatchingPanel
                  key={transactionsForPanel.map((t) => t.bankTxnId).join(",")}
                  transactions={transactionsForPanel}
                  onRowSelectionChange={setSelectedInvoiceIds}
                  rowSelection={selectedInvoiceIds}
                  onMatch={handleMatch}
                  onUnmatch={handleUnmatch}
                  onClose={handleClosePanel}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <UploadProgressBox />
    </>
  );

  async function handleMarkAsLost(bankTxnIds?: string[]): Promise<void> {
    const txnIds = bankTxnIds ?? transactionsForPanel.map((t) => t.bankTxnId);

    const exitingRowIds: Record<string, boolean> = {};
    transactionsForPanel.forEach((txn) => {
      const rowId = transactionTodos.table.getRow(txn.bankTxnId, true)?.id;
      if (rowId) {
        exitingRowIds[rowId] = true;
      }
    });

    transactionTodos.table.setExitingRow(() => exitingRowIds);

    await new Promise((resolve) => setTimeout(resolve, 300));

    await updateBankTransaction({
      bankTransactionIds: txnIds,
      updatedFields: {
        smbAttachmentStatus: SmbAttachmentStatus.LOST,
      },
    });

    await new Promise((resolve) => setTimeout(resolve, 100));

    transactionTodos.table.setExitingRow(() => ({}));
  }

  async function handleMarkAsReviewed(bankTxnIds?: string[]): Promise<void> {
    const txnIds = bankTxnIds ?? transactionsForPanel.map((t) => t.bankTxnId);

    const exitingRowIds: Record<string, boolean> = {};
    transactionsForPanel.forEach((txn) => {
      const rowId = transactionTodos.table.getRow(txn.bankTxnId, true)?.id;
      if (rowId) {
        exitingRowIds[rowId] = true;
      }
    });

    transactionTodos.table.setExitingRow(() => exitingRowIds);

    await new Promise((resolve) => setTimeout(resolve, 300));

    await approveSmbTransactionAttachments({
      bankTransactionIds: txnIds,
      smbApproved: true,
    });

    await new Promise((resolve) => setTimeout(resolve, 100));

    transactionTodos.table.setExitingRow(() => ({}));
  }

  async function handleMatch(invoiceIds?: string[]) {
    const relevantInvoiceIds = invoiceIds ?? Object.keys(selectedInvoiceIds);

    const bankTxnIds = transactionsForPanel.map((t) => t.bankTxnId);

    const exitingRowIds: Record<string, boolean> = {};
    transactionsForPanel.forEach((txn) => {
      const rowId = transactionTodos.table.getRow(txn.bankTxnId, true)?.id;
      if (rowId) {
        exitingRowIds[rowId] = true;
      }
    });

    transactionTodos.table.setExitingRow(() => exitingRowIds);

    setSelectedInvoiceIds({});
    transactionTodos.table.resetRowSelection();

    await new Promise((resolve) => setTimeout(resolve, 300));

    await addInvoiceToTransaction({
      bankTxnIds,
      invoiceIds: relevantInvoiceIds,
    });

    await new Promise((resolve) => setTimeout(resolve, 100));

    transactionTodos.table.setExitingRow(() => ({}));
  }

  async function handleUnmatch(invoiceIds?: string[]) {
    const relevantInvoiceIds = invoiceIds ?? Object.keys(selectedInvoiceIds);

    const bankTxnIds = transactionsForPanel.map((t) => t.bankTxnId);

    await deleteInvoiceFromTransaction({
      bankTxnIds,
      invoiceIds: relevantInvoiceIds,
    });
  }

  function handleClosePanel() {
    setSelectedTransactionIds({});
    setSelectedInvoiceIds({});
  }
}
